<template>
  <div>
    <b-modal
      id="calendarSubjectSettingsLogModal"
      ref="calendarSubjectSettingsLogModal"
      ok-variant="secondary"
      size="lg"
      :no-footer="true"
      @hide="onHideModal"
      @close="onHideModal"
    >
      <template #title>
        <i class="fa fa-history me-1"></i> {{ $t("Automarking settings history") }}
      </template>

      <div v-for="log in calendarSubjectSettingsLogs" :key="log.id" class="mb-3 border rounded p-3">
        <div class="d-flex justify-content-between mb-2">
          <small class="text-muted fw-semibold"
            ><i class="fa fa-clock me-1"></i>{{ log.changedAt }}</small
          >
          <small class="text-muted">{{ log.changedBy || "-" }}</small>
        </div>

        <dl class="mb-0 row row-cols-1 g-1">
          <template v-for="field in getChangedFields(log)" :key="field.key">
            <div class="col">
              <dt class="text-muted fw-normal" style="font-size: 0.8rem">{{ field.label }}</dt>
              <dd class="mb-0 d-flex align-items-center flex-wrap gap-1">
                <span class="text-danger">{{ field.oldValue }}</span>
                <span class="text-muted mx-1">→</span>
                <span class="text-success">{{ field.newValue }}</span>
              </dd>
            </div>
          </template>

          <div v-if="getChangedFields(log).length === 0" class="col">
            <small class="text-muted fst-italic">{{ $t("No changes recorded") }}</small>
          </div>
        </dl>
      </div>
    </b-modal>
  </div>
</template>

<script>
export default {
  name: "CalendarSubjectSettingsLogModal",

  props: {
    show: {
      type: Boolean,
      default: false,
      required: true,
    },

    calendarSubjectSettingsLogs: {
      type: Array,
      default: () => [],
    },
  },

  emits: ["hide"],

  watch: {
    show(newValue) {
      if (newValue) {
        this.$refs.calendarSubjectSettingsLogModal.show();
      }
    },
  },

  methods: {
    onHideModal() {
      this.$emit("hide");
    },

    formatAutomarkPresences(value) {
      return value ? this.$t("Enabled") : this.$t("Disabled");
    },

    formatWeekdays(weekdays) {
      if (!weekdays) return "-";

      return (
        Object.entries(weekdays)
          .filter(([, active]) => active)
          .map(([key]) => this.$t(`automarked_weekday_${key}`))
          .join(", ") || this.$t("None")
      );
    },

    formatInterval(interval) {
      if (!interval) return "-";
      return this.$t(`interval_${interval}`);
    },

    formatMealTypesCount(mealTypes) {
      if (!mealTypes) return "-";
      return `${mealTypes.length}`;
    },

    weekdaysEqual(a, b) {
      if (!a || !b) return a === b;
      return JSON.stringify(a) === JSON.stringify(b);
    },

    mealTypesEqual(a, b) {
      if (!a || !b) return a === b;
      if (a.length !== b.length) return false;
      const sorted = (arr) =>
        [...arr].sort((x, y) => x.mealTypeId - y.mealTypeId || x.portionSizeId - y.portionSizeId);
      return JSON.stringify(sorted(a)) === JSON.stringify(sorted(b));
    },

    getChangedFields(log) {
      const fields = [];

      if (log.oldAutomarkPresences !== log.newAutomarkPresences) {
        fields.push({
          key: "automarkPresences",
          label: this.$t("Automark presences"),
          oldValue: this.formatAutomarkPresences(log.oldAutomarkPresences),
          newValue: this.formatAutomarkPresences(log.newAutomarkPresences),
        });
      }

      if (!this.weekdaysEqual(log.oldAutomarkedWeekdays, log.newAutomarkedWeekdays)) {
        fields.push({
          key: "weekdays",
          label: this.$t("Automarked weekdays"),
          oldValue: this.formatWeekdays(log.oldAutomarkedWeekdays),
          newValue: this.formatWeekdays(log.newAutomarkedWeekdays),
        });
      }

      if (!this.mealTypesEqual(log.oldAutoselectedMealTypes, log.newAutoselectedMealTypes)) {
        fields.push({
          key: "mealTypes",
          label: this.$t("Selected meal types count"),
          oldValue: this.formatMealTypesCount(log.oldAutoselectedMealTypes),
          newValue: this.formatMealTypesCount(log.newAutoselectedMealTypes),
        });
      }

      if (log.oldAutoselectionInterval !== log.newAutoselectionInterval) {
        fields.push({
          key: "interval",
          label: this.$t("Autoselection interval"),
          oldValue: this.formatInterval(log.oldAutoselectionInterval),
          newValue: this.formatInterval(log.newAutoselectionInterval),
        });
      }

      if (log.oldAutomarkStartDate !== log.newAutomarkStartDate) {
        fields.push({
          key: "startDate",
          label: this.$t("Automark start date"),
          oldValue: log.oldAutomarkStartDate || "-",
          newValue: log.newAutomarkStartDate || "-",
        });
      }

      if (log.oldAutomarkEndDate !== log.newAutomarkEndDate) {
        fields.push({
          key: "endDate",
          label: this.$t("Automark end date"),
          oldValue: log.oldAutomarkEndDate || "-",
          newValue: log.newAutomarkEndDate || "-",
        });
      }

      return fields;
    },
  },
};
</script>
